import { useEffect } from "react";

import { queryClient } from "./queryClient";

// What a change touches, and so which reads on screen fetch again (plan 019
// §3.5): your shelf (entries, logs, ratings, reviews, Continue, favourites)
// or your lists. A read hook names the ones it shows (`about`); a write says
// which it changed (`changed`). A work's modal sits over the page it came
// from, which stays mounted, so this is how that page shows an edit made in
// the modal once it closes.
export type Topic = "shelf" | "lists";

type Listener = { about: Topic[]; run: () => void };
const listeners = new Set<Listener>();

/**
 * Say what a write changed: every read on screen that shows it fetches
 * again, and every answer no page shows is forgotten, so a page that comes
 * back after the change never shows it as it was.
 */
export const changed = (...topics: Topic[]): void => {
  queryClient.removeQueries({ predicate: (query) => !query.isActive() });
  for (const listener of Array.from(listeners)) {
    if (listener.about.some((topic) => topics.includes(topic))) listener.run();
  }
};

/** Run `run` whenever something in `about` changes; none, never. */
export const useChanged = (about: Topic[] | undefined, run: () => void) => {
  // By value, so a hook's literal list doesn't resubscribe every render.
  const key = about?.join(",") ?? "";
  useEffect(() => {
    if (!key) return;
    const listener = { about: key.split(",") as Topic[], run };
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, [key, run]);
};
